import React, { createContext, useCallback, useContext, useState } from "react";
import {
  DrillDownData,
  DrillDownModal,
  DrillDownType,
} from "../components/modals/DrillDownModal";

interface DrillDownContextValue {
  isOpen: boolean;
  drillDownData: DrillDownData | null;
  canGoBack: boolean;
  showDrillDown: (type: DrillDownType, data: DrillDownData) => void;
  closeDrillDown: () => void;
  pushDrillDown: (data: DrillDownData) => void;
  popDrillDown: () => void;
}

const DrillDownContext = createContext<DrillDownContextValue | null>(null);

export function DrillDownProvider({ children }: { children: React.ReactNode }) {
  const [drillDownData, setDrillDownData] = useState<DrillDownData | null>(
    null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState<DrillDownData[]>([]);

  const showDrillDown = useCallback(
    (_type: DrillDownType, data: DrillDownData) => {
      setDrillDownData(data);
      setIsOpen(true);
    },
    [],
  );

  const closeDrillDown = useCallback(() => {
    setIsOpen(false);
    setHistory([]);
    setTimeout(() => setDrillDownData(null), 300);
  }, []);

  const pushDrillDown = useCallback(
    (data: DrillDownData) => {
      if (drillDownData) {
        setHistory((prev) => [...prev, drillDownData]);
      }
      setDrillDownData(data);
    },
    [drillDownData],
  );

  const popDrillDown = useCallback(() => {
    if (history.length > 0) {
      const previous = history[history.length - 1];
      setHistory((prev) => prev.slice(0, -1));
      setDrillDownData(previous);
    }
  }, [history]);

  const canGoBack = history.length > 0;

  return (
    <DrillDownContext.Provider
      value={{
        isOpen,
        drillDownData,
        canGoBack,
        showDrillDown,
        closeDrillDown,
        pushDrillDown,
        popDrillDown,
      }}
    >
      {children}
      <DrillDownModal
        isOpen={isOpen}
        onClose={closeDrillDown}
        data={drillDownData}
      />
    </DrillDownContext.Provider>
  );
}

export function useDrillDownContext(): DrillDownContextValue {
  const context = useContext(DrillDownContext);
  if (!context) {
    throw new Error(
      "useDrillDownContext must be used within a DrillDownProvider",
    );
  }
  return context;
}
